// Pre-population of inspection responses from prior data sources.
//
// The component fires an effect every time any of (lastAnswers, CA table,
// lastInspection, schema) lands. Each run rebuilds the responses dict, so the
// computation has to be deterministic regardless of source arrival order — a
// stale source 3 entry must never beat a fresh source 2 entry. This module
// centralizes the merge so the ordering invariants stay testable.

import type { InspectionCategory } from '../../utils/schemaQuestionMapper';

export type PrePopResponse = {
  questionId: string;
  value: boolean | string;
  notes?: string;
  requiresCorrection?: boolean;
  correctionDeadline?: string;
  timestamp: string;
};

export type LastAnswers = Record<
  string,
  {
    value: string | null;
    notes: string | null;
  }
>;

export type CorrectiveActionRow = {
  id: string;
  question_id: string;
  status: string;
  last_answer: boolean;
  notes: string | null;
  location: string | null;
  deadline: string | null;
};

export type LastInspectionFinding = {
  source_checklist_question_id?: string | null;
  item?: string;
  condition?: string;
  requires_correction?: boolean;
  correction_notes?: string | null;
  correction_deadline?: string | null;
};

export type PrePopInput = {
  lastAnswers?: LastAnswers | null;
  correctiveActions?: CorrectiveActionRow[] | null;
  lastInspectionFindings?: LastInspectionFinding[] | null;
  inspectionCategories: InspectionCategory[];
  now?: () => string;
};

export function prePopulateResponses(
  input: PrePopInput
): Record<string, PrePopResponse> {
  const {
    lastAnswers,
    correctiveActions,
    lastInspectionFindings,
    inspectionCategories,
  } = input;
  const timestamp = (input.now ?? (() => new Date().toISOString()))();

  const out: Record<string, PrePopResponse> = {};
  if (inspectionCategories.length === 0) return out;

  const allQuestions = inspectionCategories.flatMap((cat) => cat.questions);
  const questionIds = new Set(allQuestions.map((q) => q.id));

  const openCAQuestionIds = new Set(
    (correctiveActions ?? [])
      .filter((a) => a.status === 'open' || a.status === 'in_progress')
      .map((a) => a.question_id)
  );

  // Source 1 — checklist_responses (lastAnswersData)
  if (lastAnswers) {
    Object.entries(lastAnswers).forEach(([qid, ans]) => {
      if (!questionIds.has(qid)) return;
      const raw = (ans.value || '').toLowerCase();
      let value: boolean | string;
      if (raw === 'yes') value = true;
      else if (raw === 'no') value = false;
      else if (raw === 'na' || raw === 'n/a') value = 'na';
      else return;
      const hasOpenCA = openCAQuestionIds.has(qid);
      if (value === false && !hasOpenCA) {
        value = true;
      } else if (value !== false && hasOpenCA) {
        value = false;
      }
      out[qid] = {
        questionId: qid,
        value,
        notes: ans.notes || undefined,
        requiresCorrection: hasOpenCA,
        timestamp,
      };
    });
  }

  // Source 2 — corrective_actions table (last_answer is the prior switch
  // state). Force No whenever any open CA exists for the question, regardless
  // of which CA row gets iterated first. Without this, a resolved row arriving
  // before any open row would set value=true and the early-return on subsequent
  // rows would lock it in.
  if (correctiveActions?.length) {
    correctiveActions.forEach((action) => {
      if (!questionIds.has(action.question_id)) return;
      if (out[action.question_id]) return;
      const hasOpenCA = openCAQuestionIds.has(action.question_id);
      let value: boolean | string = action.last_answer;
      if (hasOpenCA) {
        value = false;
      } else if (value === false) {
        value = true;
      }
      out[action.question_id] = {
        questionId: action.question_id,
        value,
        notes: action.notes || undefined,
        requiresCorrection: hasOpenCA,
        correctionDeadline: action.deadline || undefined,
        timestamp,
      };
    });
  }

  // Source 3 — lastInspection.findings. Same flip invariant: even if the
  // finding's condition was 'good', an open CA on that question means No.
  if (lastInspectionFindings?.length) {
    lastInspectionFindings.forEach((finding) => {
      let matchingQuestion = finding.source_checklist_question_id
        ? allQuestions.find(
            (q) => q.id === finding.source_checklist_question_id
          )
        : undefined;
      if (!matchingQuestion) {
        matchingQuestion = allQuestions.find(
          (q) => q.question === finding.item
        );
      }
      if (!matchingQuestion) return;

      const qid = matchingQuestion.id;
      const hasOpenCA = openCAQuestionIds.has(qid);
      const existing = out[qid];
      if (existing) {
        // Overlay correction context only while a CA is actually open —
        // finding.requires_correction alone is history once its CAs are
        // resolved (same invariant as the value flip below).
        if (hasOpenCA) {
          existing.requiresCorrection = true;
          existing.notes = finding.correction_notes || existing.notes;
          existing.correctionDeadline =
            finding.correction_deadline || existing.correctionDeadline;
        }
        return;
      }
      let value: boolean | string = finding.condition === 'good';
      if (finding.condition === 'na') {
        // A prior N/A answer stays N/A — it used to fall through the boolean
        // mapping and re-open as Yes (audit 2026-08-31: "answers from
        // previous inspection not all present").
        value = 'na';
      } else if (finding.condition === 'fair' && finding.requires_correction) {
        value = false;
      } else if (finding.condition === 'fair') {
        value = true;
      } else if (
        finding.condition === 'poor' ||
        finding.condition === 'failed'
      ) {
        value = false;
      }
      if (hasOpenCA) {
        value = false;
      } else if (value === false) {
        // Same flip invariant as Sources 1 and 2: a prior bad finding whose
        // corrective actions have all been resolved starts the new visit as
        // Yes. Without this, a resolved-out site opened with pre-set No
        // answers demanding fresh CAs (Jim, Bridgewater 2026-08-30).
        value = true;
      }
      out[qid] = {
        questionId: qid,
        value,
        notes: finding.correction_notes || undefined,
        requiresCorrection: hasOpenCA,
        correctionDeadline: finding.correction_deadline || undefined,
        timestamp,
      };
    });
  }

  return out;
}
